/**
 * 3D viewer Babylon.js implementation
 */
import { AbstractMesh, AnimationGroup, Camera, CreatePlane, CreateText, CubicEase, EasingFunction, Material, Mesh, Nullable, PickingInfo, PointerEventTypes, RenderTargetTexture, SSAORenderingPipeline, StandardMaterial, UniversalCamera, UtilityLayerRenderer, VertexBuffer } from '@babylonjs/core';
import { Animation } from '@babylonjs/core/Animations/animation';
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import '@babylonjs/core/Collisions/collisionCoordinator'; // needed by ArcRotateCamera to avoid collisions
import { Constants } from '@babylonjs/core/Engines/constants';
import { Engine } from '@babylonjs/core/Engines/engine';
import { CascadedShadowGenerator } from '@babylonjs/core/Lights/Shadows/cascadedShadowGenerator';
import { ShadowGenerator } from '@babylonjs/core/Lights/Shadows/shadowGenerator';
import '@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent'; // for shadow generator
import { DirectionalLight } from '@babylonjs/core/Lights/directionalLight';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { SceneLoader } from '@babylonjs/core/Loading/sceneLoader';
import { BackgroundMaterial } from '@babylonjs/core/Materials/Background/backgroundMaterial';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import '@babylonjs/core/Materials/Textures/Loaders/envTextureLoader'; // for loading environment files
import { CubeTexture } from '@babylonjs/core/Materials/Textures/cubeTexture';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Color3, Color4, Vector2, Viewport } from '@babylonjs/core/Maths/math';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { CreateGround } from '@babylonjs/core/Meshes/Builders/groundBuilder';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Node } from '@babylonjs/core/node';
import { Scene } from '@babylonjs/core/scene';
import '@babylonjs/loaders/glTF'; // needed for SceneLoader to load GLTF/GLB
import { GLTF2Export } from '@babylonjs/serializers/glTF';
import { Tools } from '@babylonjs/core/Misc/tools';

import debounce from 'debounce';
import toposort from 'toposort';
import earcut from "earcut";
import { diff } from 'deep-object-diff';
(window as any).earcut = earcut;

import { DEFAULT_CLICKABLE, DEFAULT_HIDE, DEFAULT_IS_BILLBOARD, DEFAULT_IS_ICON, DEFAULT_POSITION, DEFAULT_ROTATION, DEFAULT_SCALE, DEFAULT_TEXTURE_SCALE, ElementLayoutResult, VCamera, VColor, VCoord, VGroupAttributes, VGroupRelativePosition, VGroupRelativePositionSide, VGroupSpatialInfo, VLocationLoaded, VMeshRuleLoaded, VPartPositioning, VRelativePositioning, VRelativePositioningBoundingBox, VRelativePositioningMeshes, VRelativePositioningPart, VRotation, VScale, VThumbnailInfo, WatchedElementResult } from './viewer-3d-content';
import { EventEmitter, eventTypes, events } from '../app/event-emitter';
import { Viewer3D } from './viewer-3d';
import dimensionsFont from './fira-sans-light-regular.json';
import { meterToInch, formatMeterToInch } from '../util/unit-conversion';

// TODO: this should be false in the final version for performance,
// but layout it is buggy if false.
// It's compensated making a diff in the event
const TMP_LAYOUT_EVERY_FRAME = true;
const SHOW_4_DIMENSIONS = false;

const MOUSE_MAIN_BUTTON = 0;

const ORIGIN = new Vector3(0, 0, 0);
const DOWN = new Vector3(0, -1, 0);
const DLIGHT_POSITION = new Vector3(0, 200, 0);
const VORIGIN: VCoord = [ 0, 0, 0 ];
const NULL_CAMERA: VCamera = {
    target: [0,0,0],
    position: [0,0,0]
}

const CAMERA_SETTINGS = {
    speed: 2,
    wheelPrecision: 30,
    panningSensibility: 1000,
    wheelDeltaPercentage: 0,
    minZ: 0.01,
    maxZ: 10000,
    checkCollisions: true,
    collisionRadius: new Vector3(0.3, 0.3, 0.3),
    upperBetaLimit: (Math.PI/2),
    lowerRadiusLimit: 1,
    upperRadiusLimit: 15,
    fitAngle: Math.PI/8,
}
const TOP_CAMERA_SETTINGS = {
    minZ: 0.01,
    maxZ: 10000,
    wheelDeltaPercentage: 0.05,
    alpha: Math.PI/2,
    beta: Math.PI/2,
    radius: 30,
    lowerRadiusLimit: 8,
    upperRadiusLimit: 90,
    fov: 0.2
}

const CAMERA_FIT_MARGIN_FACTOR = 1.25;

const CAMERA_FPS = 30;
const CAMERA_RESET_DURATION = 2;

const ZOOM_RATIO = 0.2;
const MOVE_THRESHOLD = 0.01;

const BASE_TEXTURE_SCALE = 6;
const ENVIRONMENT_INTENSITY = 0.2;
const HEMISPHERIC_LIGHT_INTENSITY = 1.25;
const HEMISPHERIC_LIGHT_DIFFUSE = new Color3(0.95,0.95,0.95);
//const HEMISPHERIC_LIGHT_GROUND = new Color3(0.61,0.61,0.61);
const HEMISPHERIC_LIGHT_GROUND = new Color3(0.02,0.02,0.02);
const SHADOW_CATCHER_ALPHA = 0.15;

const DIMENSION_LINE_WIDTH = 0.025;
const DIMENSION_HEIGHT = 0.01;
const SELECTION_COLOR = new Color3(1, 0, 0);
const GLOBAL_DIMENSIONS_COLOR = new Color3(0.5, 0.5, 0.5);
const DIMENSION_TEXT_SIZE = 0.1;
const DIMENSION_TEXT_DEPTH = 0.001;
const DIMENSION_TEXT_OFFSET = 0.09;

// node type (group, part)
const ATTR_TYPE = 'type';
// exclude this node from the bounding box calculation (utility)
const ATTR_USE_IN_BBOX = 'useInBoundingBox';
// exclude this node from the bounding box calculation (mesh rule - cushions, etc.)
const ATTR_EXCLUDE_FROM_BBOX_MESH_RULE = 'useInBoundingBoxMR';

// show a dimensions box in this group
const ATTR_SHOW_DIMENSIONS = 'showDimensions';
// dimensions previously applied, to minify creation/deletion
const ATTR_SHOW_DIMENSIONS_APPLIED = 'showDimensionsApplied';
// forced dimensions (in case bounding box is not enough)
const ATTR_DIMENSIONS = 'dimensions';
// true if the mesh is a dimension mesh
const ATTR_IS_DIMENSION = 'isDimension';
// data to send if clicked
const ATTR_DATA = 'data';
// if a transform node has this to true, it can be grabbed and moved
const ATTR_MOVEABLE = 'moveable';
// controls if it is clickable or not
const ATTR_CLICKABLE = 'clickable';
// texture scale already applied
const ATTR_UV_ALREADY_SCALED = 'uvScaled';
// this node is a position referece, should be copied after relayout or move
const ATTR_POSITIONED_BY = 'positionedBy';
// this makes the element fixed fize, independent of camera distance
const ATTR_FIXED_SIZE_FACTOR = 'fixedSizeFactor';
// this node should be hidden in a snapshot
const ATTR_HIDE_IN_SNAPSHOT = 'hideInSnapshot';



// type part
const TYPE_PART = 'part';
// type group
const TYPE_GROUP = 'group';

// global dimensions object
const GLOBAL_DIMENSIONS_ID = '--global-dimensions';
// shadow catcher object
const SHADOW_CATCHER_ID = '--shadow-catcher';
// orbiting camera
const ORBITING_CAMERA_ID = '--camera';
// top camera
const TOP_CAMERA_ID = '--top-camera';
// camera for the thumbnail
const THUMBNAIL_CAMERA_ID = '--thumbnail-camera';
// clear thumbnail scene camera
const CLEAR_THUMBNAIL_CAMERA_ID = '--clear-thumbnail-camera';
// billboards
const BILLBOARDS_ID = '--billboards';

// This is counted in frames
const FRAMES_UPDATE_LAYOUT = 60;

// shadow catcher
const SC_DEFAULT_SIZE = 20;

const REF_PREFIX = 'ref--';


export type V3BShadowGeneratorType = 'none' | 'normal' | 'cascaded';
export type V3BShadowFiltering = 'none' | 'poisson' | 'exp-sm' | 
                                 'blur-exp-sm' | 'blur-exp-kernel' |
                                 'close-exp' | 'pcf' | 'contact-hard';

export interface V3BQualitySettings {
    shadowGeneratorType: V3BShadowGeneratorType
    shadowResolution: number
    shadowFiltering: V3BShadowFiltering
}

export const V3BDefaultQualitySettings: V3BQualitySettings = {
    shadowGeneratorType: 'normal',
    shadowResolution: 512,
    shadowFiltering: 'exp-sm'
}

export interface V3BConfiguration {
    // initial scene
    defaultScene: string
    // show or hide the reference objects (not used)
    hideReferences: boolean
    // show materials as wireframe (for debugging purposes)
    wireframe: boolean
    // send EventEmitter events
    broadcastEvents: boolean
    // True if it is an offscreen image generation
    offscreen: boolean
}

/**
 * Viewer3D implementation with Babylon.js
 */
export class Viewer3DBabylon implements Viewer3D {

    // Canvas element
    private canvas: HTMLCanvasElement|OffscreenCanvas;
    // Babylon engine. There's one engine per canvas
    private engine: Engine;
    // Offscreen: Indicator that this is an offscreen image generation
    private offscreen: boolean

    // Collection of scenes. It may be used to maintain multiple scenes.
    // Data related to each scene is stored in a SceneContext object
    private scenes: Map<string,SceneContext>;
    // Current scene id. This is the scene that's being show on the canvas
    private currentScene: string;

    // True if there's a batch update. This delays certain operations, like layout
    // until the end, for performance reasons and to avoid visual glitches
    private inUpdate: boolean;

    // Do the actual image painting (render). This can be suspended while
    // loading, to not show all the intermediate states
    private doRender: boolean;

    // Supress any of the tasks of the render loop. The render loop
    // does also layout and other tasks. This flag, interrupts also that,
    // and it's used to prevent de renderLoop to interfere in taking
    // an snapshot.
    private nullRenderLoop: boolean;


    // Wireframe mode (for debugging)
    private wireframe: boolean;

    // Generate events
    private broadcastEvents: boolean;

    private hideReferences: boolean;
    private cameraOffset: Vector2;
    private moveStartingPoint: Nullable<Vector3>;
    private moveDiffReference: Nullable<Vector3>;
    private moveGroup?: Nullable<TransformNode>;    

    private qualitySettings: V3BQualitySettings;

    private animationPercentagesByScene: Map<string, number> = new Map();

    private debouncedUpdateLayout: (sceneCtx: SceneContext) => void;    

    // when doing layout, update the dimensions of this nodes
    private watchDimensionsNodes: string[]

    // unit system for dimensions display ('cm' or 'inch')
    private unitSystem: 'cm' | 'inch' = 'cm';

    // needed to clear the thumbnails
    private thumbBackgroundScene: Scene;

    // hack to comunicate relayout, but not too much
    private nextRelayoutUpdateIn: number;

    // keep track of touchpoints, to prevent interaction of pinching and dragging
    private touchPoints: number;



    // ==== construction / destruction - should be used only in main files ====

    constructor(canvas: HTMLCanvasElement|OffscreenCanvas, config: V3BConfiguration) {
        this.canvas = canvas;
        this.scenes = new Map();
        this.engine = new Engine(canvas);
        this.inUpdate = false;
        this.doRender = false;
        this.nullRenderLoop = false;
        this.qualitySettings = V3BDefaultQualitySettings;

        this.hideReferences = config.hideReferences;
        this.currentScene = config.defaultScene;
        this.wireframe = config.wireframe;
        this.broadcastEvents = config.broadcastEvents;
        this.offscreen = config.offscreen;

        this.cameraOffset = new Vector2(0,0);
        this.moveStartingPoint = null;
        this.moveDiffReference = null;
        this.debouncedUpdateLayout = debounce(this.updateLayout.bind(this), 100);
        this.animationPercentagesByScene = new Map();

        this.thumbBackgroundScene = this.createClearThumbnailScene();

        this.nextRelayoutUpdateIn = 0;
        this.watchDimensionsNodes = [];
        this.touchPoints = 0;

        if(!this.offscreen) {
            this.engine.runRenderLoop(this.renderLoop.bind(this));
        }

    }

    dispose() {
        for(const sceneId of this.scenes.keys()) {
            this.clearScene(sceneId);
        }
        if(!this.offscreen) {
            this.doRender = true;
            this.renderLoop();    
        }
        this.engine.dispose();
    }

    // ==== Viewer3D implementation ====

    setCurrentScene(sceneId: string) {
        this.switchOffScene(sceneId);
        this.currentScene = sceneId;
        const currentSceneCtx = this.scenes.get(sceneId);
        if(currentSceneCtx) {
            currentSceneCtx.camera.attachControl(this.canvas);
            this.setViewport(currentSceneCtx.camera);
            this.setViewport(currentSceneCtx.topCamera);
        }
    }

    createScene(sceneId: string) {
        const scene = new Scene(this.engine);
        const utilLayer = new UtilityLayerRenderer(scene);
        scene.collisionsEnabled = true;
        scene.onPointerObservable.add((pointerInfo) => {
            if(this.currentScene !== sceneId) return;
            switch (pointerInfo.type) {

                case PointerEventTypes.POINTERPICK:
                    this.pointerPick(pointerInfo.pickInfo);
                    break;

                case PointerEventTypes.POINTERDOWN:
                    this.touchPoints += 1;
                    if(this.touchPoints > 1)  {
                        this.pointerUp();
                    } else {
                        this.pointerDown(pointerInfo.pickInfo)
                    }
                    break;

                case PointerEventTypes.POINTERUP:
                    this.touchPoints -= 1;
                    if(this.touchPoints < 0) {
                        this.touchPoints = 0;
                    }
                    this.pointerUp();
                    break;

                case PointerEventTypes.POINTERMOVE:          
                    this.pointerMove();
                    break;
            }
        });

        const camera = this.create3DCamera(scene);
        const topCamera = this.createTopCamera(scene);
        const billboards = new TransformNode(BILLBOARDS_ID, scene);
        //this.addSSAO(scene, [ camera, topCamera ]);
        this.scenes.set(sceneId, {
            sceneId,
            scene,
            utilLayer,
            billboards,
            camera,
            topCamera,
            layoutRules: [],
            pendingLayout: false,
            pendingGroupDimensionsUpdate: new Set(),
            pendingGlobalDimensions: false,
            groupLayoutRules: [],
            globalDimensions: false,
            showAsThumbnail: false,
            thumbnailPosition: [ 0, 0],
            thumbnailSize: [ 1, 1 ]
        });
        if(!this.animationPercentagesByScene.has(sceneId)) {
            this.animationPercentagesByScene.set(sceneId, 0);
        }
        if(sceneId === this.currentScene) {
            this.setCurrentScene(sceneId);
        }

    }

    deleteScene(sceneId: string): void {
        if(this.currentScene == sceneId) {
            this.switchOffScene(this.currentScene);
        }
        const scene = this.scenes.get(sceneId);
        if(scene) {
            this.scenes.delete(sceneId);
            scene.scene.dispose();
        }
        this.scenes.delete(sceneId);
    }

    setBackgroundColor(sceneId: string, color: VColor|undefined) {
        const sceneCtx = this.scenes.get(sceneId);
        if(!sceneCtx) return;
        sceneCtx.scene.clearColor = color ? new Color4(...color) : new Color4(1,1,1,0);
    }

    setCamera(_sceneId: string, camera: VCamera, immediate: boolean = true) {
        const sceneId = _sceneId === '*CURRENT' ? this.currentScene : _sceneId;
        if(!sceneId) return;
        const sceneCtx = this.scenes.get(sceneId);
        if(!sceneCtx) return;
        const dstPosition = toVector3(camera.position);
        const dstTarget = toVector3(camera.target);
        if(immediate) {
            sceneCtx.camera.setTarget(dstTarget);
            sceneCtx.camera.setPosition(dstPosition);
        } else {
            this.animateCamera(sceneCtx.camera, dstTarget, dstPosition);
        }
    }

    useOrbitCamera(sceneId: string): void {

        const sceneCtx = this.scenes.get(sceneId);
        if(!sceneCtx) return;

        const scene = sceneCtx.scene;
        if(scene.activeCamera) {
            scene.activeCamera.detachControl();
        }
        
        scene.activeCamera = sceneCtx.camera;
        sceneCtx.camera.attachControl(this.canvas);
        this.setViewport(sceneCtx.camera);
    }

    use2DCamera(sceneId: string): void {

        const sceneCtx = this.scenes.get(sceneId);
        if(!sceneCtx) return;

        const scene = sceneCtx.scene;
        if(scene.activeCamera) {
            scene.activeCamera.detachControl();
        }

        const camera = sceneCtx.topCamera;
        scene.activeCamera = camera;
        camera.attachControl(this.canvas);
        this.setViewport(camera);

        this.fitTopCamera(sceneCtx);

    }

    fitContent(sceneId: string, immediate: boolean): void {
        const sceneCtx = this.scenes.get(sceneId);
        if(!sceneCtx) return;
        if(sceneCtx.scene.activeCamera === sceneCtx.camera) {
            this.fit3DCamera(sceneCtx, immediate);
        } else {
            this.fitTopCamera(sceneCtx);
        }
    }

    setThumbnailView(sceneId: string, thumbnailInfo: VThumbnailInfo|undefined): void {
        const sceneCtx = this.scenes.get(sceneId);
        if(!sceneCtx) return;
        if(sceneCtx.thumbnailCamera) {
            sceneCtx.thumbnailCamera.dispose();
        }
        if(thumbnailInfo) {
            const { show, camera, position, size } = thumbnailInfo;
            sceneCtx.showAsThumbnail = show;
            if(thumbnailInfo.show) {
                sceneCtx.thumbnailCamera = new ArcRotateCamera(THUMBNAIL_CAMERA_ID, 0, 0, 1, ORIGIN, sceneCtx.scene);
                sceneCtx.thumbnailCamera.setTarget(toVector3(camera.target));
                sceneCtx.thumbnailCamera.setPosition(toVector3(camera.position));
                sceneCtx.thumbnailCamera.minZ = CAMERA_SETTINGS.minZ;
                sceneCtx.thumbnailCamera.maxZ = CAMERA_SETTINGS.maxZ;
                sceneCtx.thumbnailPosition = position;
                sceneCtx.thumbnailSize = size;
            }
            this.resizeAllThumbnails();
        } else {
            sceneCtx.showAsThumbnail = false;            
        }
    }

    deleteItem(sceneId: string, itemId: string) {
        const sceneCtx = this.scenes.get(sceneId);
        if(!sceneCtx) return;
        const node = sceneCtx.scene.getNodeById(itemId);
        const type = getMetadataAttr(node, ATTR_TYPE);
        if(node instanceof TransformNode) {
            node.dispose();
        }
        if(type === TYPE_PART) {
            sceneCtx.layoutRules = sceneCtx.layoutRules.filter(r => r.partId === itemId);
        } else if(type === TYPE_GROUP) {
            sceneCtx.groupLayoutRules = sceneCtx.groupLayoutRules.filter(r => r.groupId !== itemId);
        }
        this.updateLayout(sceneCtx);
    }

    setShadowCatcher(sceneId: string, on: boolean) {
        const sceneCtx = this.scenes.get(sceneId);
        if(!sceneCtx) return;
        const { scene } = sceneCtx;

        if(on) {
            // Our built-in 'ground' shape.
            const groundMaterial = this.getShadowCatcherMaterial(sceneCtx);
            const ground = CreateGround(SHADOW_CATCHER_ID, { width: SC_DEFAULT_SIZE, height: SC_DEFAULT_SIZE, subdivisions: 1 }, scene);
            ground.material = groundMaterial;
            ground.receiveShadows = true;
            ground.metadata = { [ATTR_USE_IN_BBOX]: false };
            sceneCtx.ground = ground;
        } else {
            const node = scene.getNodeById(SHADOW_CATCHER_ID);
            node?.dispose();
        }
    }

    createGroup(sceneId: string, parentId: string | undefined, groupId: string): void {
        const sceneCtx = this.scenes.get(sceneId);
        if(!sceneCtx) return;
        const scene = sceneCtx.scene;
        const node = new TransformNode(groupId, scene);
        this.assignParent(node, scene, parentId);
        node.id = groupId;
        node.metadata = { [ATTR_TYPE]: TYPE_GROUP, parentId };
        this.updateLayout(sceneCtx);
    }

    setGroupAttributes(sceneId: string, groupId: string, attributes: VGroupAttributes): void {
        const sceneCtx = this.scenes.get(sceneId);
        if(!sceneCtx) return;
        const { scene } = sceneCtx;
        const node = scene.getTransformNodeById(groupId);
        if(node) {
            setMetadataAttr(node, ATTR_DATA, attributes.data);
            setMetadataAttr(node, ATTR_USE_IN_BBOX, attributes.useInBoundingBox);
            setMetadataAttr(node, ATTR_MOVEABLE, attributes.moveable);
            setMetadataAttr(node, ATTR_SHOW_DIMENSIONS, attributes.showDimensions);
            setMetadataAttr(node, ATTR_DIMENSIONS, attributes.dimensions);
            setMetadataAttr(node, ATTR_CLICKABLE, attributes.clickable);
            setMetadataAttr(node, ATTR_HIDE_IN_SNAPSHOT, attributes.hideInSnapshot);
            this.updateLayout(sceneCtx);
            this.updateGroupDimensions(sceneCtx, groupId);
        }
    }

    setGroupPosition(sceneId: string, groupId: string, _position: VGroupSpatialInfo): void {

        // defaults
        const position = _position.position ?? DEFAULT_POSITION;
        const rotation = _position.rotation ?? DEFAULT_ROTATION;
        const scale = _position.scale ?? DEFAULT_SCALE;
        const isBillboard = _position.isBillboard ?? DEFAULT_IS_BILLBOARD;
        const fixedSizeFactor = _position.fixedSizeFactor;

        const sceneCtx = this.scenes.get(sceneId);
        if(!sceneCtx) return;
        const { scene } = sceneCtx;

        let node = scene.getTransformNodeById(groupId);
        if(!node) return;

        if(isBillboard) {
            const oldParent = node.parent;
            const dummy = new TransformNode(node.id + '--dummy', scene);
            dummy.parent = oldParent;

            node.billboardMode = TransformNode.BILLBOARDMODE_ALL;
            node.parent = sceneCtx.billboards;
            setMetadataAttr(node, ATTR_POSITIONED_BY, dummy.id);
            if(fixedSizeFactor) {
                setMetadataAttr(node, ATTR_FIXED_SIZE_FACTOR, fixedSizeFactor);
            }

            node = dummy;
        }
        if(!isBillboard && fixedSizeFactor) {
            setMetadataAttr(node, ATTR_FIXED_SIZE_FACTOR, fixedSizeFactor);
        }

        sceneCtx.groupLayoutRules = sceneCtx.groupLayoutRules.filter(r => r.groupId !== groupId);
        if(Array.isArray(position)) {
            node.position = toVector3(position);
        } else {
            sceneCtx.groupLayoutRules.push({ groupId: node.id, position });
        }

        node.rotation = toRotation(rotation);
        node.scaling = toScale(scale);

        this.updateLayout(sceneCtx);
        this.updateGroupDimensions(sceneCtx, groupId);
    }

    async createPart(sceneId: string, parentId: string|undefined, partId: string, location: VLocationLoaded): Promise<void> {

        const sceneCtx = this.scenes.get(sceneId);
        if(!sceneCtx) return;
        const { scene, shadowgen } = sceneCtx;

        const node = new TransformNode(partId, scene);
        this.assignParent(node, scene, parentId);
        node.id = partId;
        node.metadata = { type: 'part', parentId };

        const container = await SceneLoader.LoadAssetContainerAsync('', location.url, scene, null, `.${location.ext}`);
        for(const ag of container.animationGroups) {
            ag.start();
            ag.goToFrame(ag.from);
            ag.pause();
            ag.metadata = { partId };
        }
        container.addAllToScene();

        for(const n of container.getNodes()) {

            const childMeshes = n.getChildMeshes();

            for(const m of childMeshes) {
                if(shadowgen) {
                    shadowgen.addShadowCaster(m);
                }
                m.checkCollisions = true;
            }

            // TODO: this depends a lot on the babylon exporter, it should be more generic
            //       but not having parent is really not working
            if(n.id === '__root__') {
                this.assignParent(n, scene, partId);
                for(const m of n.getChildMeshes()) {

                    const isReference = m.name.startsWith(REF_PREFIX);
                    const enabled = node.isEnabled() && !isReference;

                    m.metadata = {
                        parentId: partId,
                        loadTime: { enabled, isReference }
                    }
                }
            }

        }

        this.updateLayout(sceneCtx);
        if(parentId) {
            this.updateGroupDimensions(sceneCtx, parentId);
        }

    }

    setPartPosition(sceneId: string, partId: string, position: VPartPositioning, rotation: VRotation, scale: VScale): void {
        const sceneCtx = this.scenes.get(sceneId);
        if(!sceneCtx) return;
        const { scene } = sceneCtx;

        sceneCtx.layoutRules = sceneCtx.layoutRules.filter(r => r.partId !== partId);
        if(!Array.isArray(position)) {
            sceneCtx.layoutRules.push({ partId, position })            
        }

        const tn = scene.getTransformNodeById(partId);
        if(tn) {
            if(Array.isArray(position)) {
                tn.position = toVector3(position);
            }
            tn.scaling = toScale(scale);
            tn.rotation = toRotation(rotation);
            this.updateLayout(sceneCtx);
            const parentId = getMetadataAttr(tn, 'parentId');
            if(parentId) {
                this.updateGroupDimensions(sceneCtx, parentId);
            }
        }
        this.updateLayout(sceneCtx);
    }

    setPartAnimation(sceneId: string, partId: string, percentage: number|undefined, fixed: boolean) {
        const sceneCtx = this.scenes.get(sceneId);
        if(!sceneCtx) return;
        const { scene } = sceneCtx;
        for(const ag of scene.animationGroups) {
            if(getMetadataAttr(ag, 'partId') === partId) {
                const appliedPercentage = percentage ?? this.animationPercentagesByScene.get(sceneId) ?? 0;
                ag.goToFrame(ag.from + (ag.to - ag.from) * ( appliedPercentage / 100));
                setMetadataAttr(ag, 'animationFixed', fixed);
            }
        }
        this.updateLayout(sceneCtx);
    }

    async setPartMeshRules(sceneId: string, itemId: string, rules: VMeshRuleLoaded[]): Promise<void> {
        const sceneCtx = this.scenes.get(sceneId);
        if(!sceneCtx) return;
        const { scene } = sceneCtx;
        const part = scene.getTransformNodeById(itemId);
        if(!part) return;

        const defaultRule = rules.find(r => r.test === '*');

        for(let mesh of part.getChildMeshes()) {

            if(getMetadataAttr(mesh, 'isReference')) {
                continue;
            }

            let appliedRule = defaultRule;
            for(let rule of rules) {
                if(rule.test === mesh.name) {
                    appliedRule = rule;                    
                }
            }
            if(appliedRule) {
                await this.applyRuleToMesh(appliedRule, mesh, itemId, scene);
            }
        }
        this.updateLayout(sceneCtx);
        const parentId = getMetadataAttr(part, 'parentId');
        if(parentId) {
            this.updateGroupDimensions(sceneCtx, parentId);
        }

    }

    // scene (light, background, etc) settings

    setHemisphereLight(sceneId: string, on: boolean): void {
        const sceneCtx = this.scenes.get(sceneId);
        if(!sceneCtx) return;
        const scene = sceneCtx.scene;
        const prev = scene.getLightById('light:hs');
        if(prev && !on) {
            // switch off
            scene.removeLight(prev);
        } else if(!prev && on) {
            const light = new HemisphericLight('hemisphere', new Vector3(0,1,0), scene);
            light.intensity = HEMISPHERIC_LIGHT_INTENSITY;
            light.diffuse = HEMISPHERIC_LIGHT_DIFFUSE;
            light.groundColor = HEMISPHERIC_LIGHT_GROUND;
            light.id = 'light:hs';
        }
        // else... do nothing, it's ok
    }

    setDirectionalLight(sceneId: string, on: boolean) {

        const sceneCtx = this.scenes.get(sceneId);
        if(!sceneCtx) return;
        const { scene } = sceneCtx;

        if(on && !sceneCtx.shadowLight) {
            const shadowLight = new DirectionalLight('dlight', DOWN, scene);
            // shadowLight.intensity = 0;
            shadowLight.intensity = 0.5;
            shadowLight.position = DLIGHT_POSITION;
            shadowLight.shadowFrustumSize = 6;
            sceneCtx.shadowLight = shadowLight;
            sceneCtx.shadowgen = this.createShadowGenerator(scene, sceneCtx.shadowLight);
            this.makeAllMeshesShadowCaster(scene, sceneCtx.shadowgen);
        } else {
            if(sceneCtx.shadowgen) {
                sceneCtx.shadowgen.dispose();
                delete sceneCtx.shadowgen;
            }
            if(sceneCtx.shadowLight) {
                sceneCtx.shadowLight.dispose();
                delete sceneCtx.shadowLight;
            }
        }

    }

    setShowGlobalDimensions(sceneId: string, on: boolean): void {
        const sceneCtx = this.scenes.get(sceneId);
        if(!sceneCtx) return;
        if(sceneCtx.globalDimensions !== on) {
            sceneCtx.globalDimensions = on;
            if(sceneCtx.globalDimensions) {
                if(this.inUpdate) {
                    sceneCtx.pendingGlobalDimensions = true;
                } else {
                    this.updateGlobalDimensions(sceneCtx);
                }    
            } else {
                this.updateGlobalDimensions(sceneCtx);
            }
        }
    }

    clearScene(sceneId: string): void {

        try {
            const sceneCtx = this.scenes.get(sceneId);
            if(!sceneCtx) return;
            const { scene } = sceneCtx;

            for(const mesh of scene.meshes.concat([])) {
                if(mesh.id !== 'shadow-catcher') {
                    scene.removeMesh(mesh, true);
                    mesh.dispose(false, true);
                }
            }
            for(const tn of scene.transformNodes.concat([])) {
                scene.removeTransformNode(tn);
                tn.dispose();
            }
            for(const m of scene.materials.concat([])) {
                if(m.name !== 'shadow-catcher-material') {
                    if(!getMetadataAttr(m, 'namedMaterial')) {
                        scene.removeMaterial(m);
                        m.dispose();    
                    }
                }
            }

            for(const ag of scene.animationGroups.concat([])) {
                scene.removeAnimationGroup(ag);
                ag.dispose();
            }
    
            sceneCtx.layoutRules = [];
            sceneCtx.groupLayoutRules = [];
            sceneCtx.globalDimensions = false;
            this.updateLayout(sceneCtx);
            this.updateGlobalDimensions(sceneCtx);

    
        } catch(err) {
            console.error('CLEAR ERROR', err);
        }

    }

    layout(sceneId: string): void {
        const sceneCtx = this.scenes.get(sceneId);
        if(!sceneCtx) return;
        this.updateLayout(sceneCtx);
    }

    resize() {
        this.engine.resize();
        this.resizeAllThumbnails();
    }

    startUpdate(): void {
        this.inUpdate = true;
    }

    endUpdate(): void {
        // do all delayed operations
        for(let ctx of Array.from(this.scenes.values())) {
            this.refreshShadowMap(ctx);
            if(ctx.pendingLayout) {
                this.doLayout(ctx, true);
                ctx.pendingLayout = false;
            }
            for(const groupId of ctx.pendingGroupDimensionsUpdate) {
                this.doGroupDimensions(ctx, groupId);
            }
            ctx.pendingGroupDimensionsUpdate.clear();
            if(ctx.pendingGlobalDimensions) {
                this.updateGlobalDimensions(ctx);
                ctx.pendingGlobalDimensions = false;
            }
        }        
        this.inUpdate = false;
    }

    startRendering(): void {
        this.doRender = true;
    }

    stopRendering(): void {
        this.doRender = false;
    }

    setQualitySettings(settings: any): void {
        let qs = <V3BQualitySettings>settings;
        let prevQS = this.qualitySettings;
        this.qualitySettings = qs;
        if(qs.shadowGeneratorType !== prevQS.shadowGeneratorType ||
           qs.shadowResolution !== prevQS.shadowResolution ||
           qs.shadowFiltering !== prevQS.shadowFiltering) {
            for(let sceneCtx of Array.from(this.scenes.values())) {
                sceneCtx.shadowgen?.dispose();
                sceneCtx.shadowgen = this.createShadowGenerator(sceneCtx.scene, sceneCtx.shadowLight);
                this.makeAllMeshesShadowCaster(sceneCtx.scene, sceneCtx.shadowgen);
            }
        }
    }

    async setEnvironment(sceneId: string, location: VLocationLoaded|undefined): Promise<void> {

        const sceneCtx = this.scenes.get(sceneId);
        if(!sceneCtx) return;
        const { scene } = sceneCtx;
        if(location) {
            let envTexture;
            await new Promise<void>((resolve, reject) => {                
                envTexture = new CubeTexture(location.url, scene, null, false, null, () => resolve(), () => reject(), Constants.TEXTUREFORMAT_RGBA, false, `.${location.ext}`);
            })            
            scene.environmentTexture = envTexture!;  
            scene.environmentIntensity = ENVIRONMENT_INTENSITY;  
        } else {
            scene.environmentTexture = null;
        }
    }

    async exportGLTF(sceneId: string, excludePatterns: (RegExp|string)[]): Promise<Blob> {
        const sceneCtx = this.scenes.get(sceneId);
        if(!sceneCtx) throw new Error('Unknown scene');
        const { scene } = sceneCtx;
        const result = await GLTF2Export.GLBAsync(scene, "viewer", {
            shouldExportNode: (n) => {
                if([SHADOW_CATCHER_ID, BILLBOARDS_ID, GLOBAL_DIMENSIONS_ID].includes(n.id)) {
                    return false;
                } else if(!n.isEnabled()) {
                    return false;
                } else {
                    for(const pattern of excludePatterns) {
                        if(pattern instanceof RegExp) {
                            if(pattern.test(n.id)) {
                                return false;
                            }
                        } else {
                            if(n.id === pattern) {
                                return false;
                            }
                        }
                    }
                    return true;
                }
                
            }
        });
        for(const key of Object.getOwnPropertyNames(result.glTFFiles)) {
            return result.glTFFiles[key] as Blob;
        }
        throw new Error('No file exported');
        
    }


    // ==== view control ====

    zoom(ratio: number): void {
        if(this.currentScene) {
            const sceneCtx = this.scenes.get(this.currentScene);
            if(!sceneCtx) return;
            const { camera } = sceneCtx;
            camera.radius /= 1 + (ratio * ZOOM_RATIO);
        }
    }

    setAllAnimationPercentages(sceneId: string, percentage: number, inmediate: boolean): void {

        this.animationPercentagesByScene.set(sceneId, percentage);

        const sceneCtx = this.scenes.get(sceneId);
        if(!sceneCtx) return;

        const { scene } = sceneCtx;
        this.doSetAllAnimationPercentages(scene, percentage);

        if(inmediate) {
            this.updateLayout(sceneCtx);
        } else {
            this.debouncedUpdateLayout(sceneCtx);
        }
        
    }

    setCameraOffset(offset: number): void {
        this.cameraOffset.x = offset;
        const sceneCtx = this.scenes.get(this.currentScene);
        if(!sceneCtx) return;
        const { camera, topCamera } = sceneCtx;
        this.setViewport(camera);
        this.setViewport(topCamera);
    }

    async render(scene: string) {
        const sceneCtx = this.scenes.get(scene);
        if(sceneCtx) {
            this.doLayout(sceneCtx, false);
            this.doCameraDependentLayout(sceneCtx);
            await sceneCtx.scene.whenReadyAsync();
            sceneCtx.scene.render();
        }
    }

    async snapshot(sceneName: string, fitContentCamera: boolean, width: number, height: number): Promise<Blob> {
        
        let scene: Scene|undefined;
        const hidden: TransformNode[] = [];

        try {
            this.nullRenderLoop = true;
            const sceneCtx = this.scenes.get(sceneName);

            if(sceneCtx) {

                scene = sceneCtx.scene;
                let camera: ArcRotateCamera;
                if(fitContentCamera) {
                    camera = this.create3DCamera(scene);
                    const [ position, target ] = this.calculateFit3DCamera(scene, camera);
                    camera.position = position;
                    camera.target = target;
                } else {
                    camera = sceneCtx.camera;
                }

                this.doLayout(sceneCtx, false);
                this.doCameraDependentLayoutAux(sceneCtx.scene, camera, false);

                for(const tn of scene.transformNodes) {
                    const hideInSnapshot = getMetadataAttr(tn, ATTR_HIDE_IN_SNAPSHOT);
                    if(tn.isEnabled(false) && hideInSnapshot) {
                        tn.setEnabled(false);
                        hidden.push(tn);
                    }
                }

                await sceneCtx.scene.whenReadyAsync();
                const dataURL = await Tools.CreateScreenshotAsync(this.engine, camera, { width, height }, 'image/jpeg', 1);
                const response = await fetch(dataURL)
                const blob = await response.blob();
                return blob;

            } else {
                throw new Error(`Unknown scene ${sceneName}`);
            }
        } finally {
            for(const tn of hidden) {
                tn.setEnabled(true);
            }
            this.nullRenderLoop = false;
        }
    }

    watchNodeDimensions(nodes: string[]): void {
        this.watchDimensionsNodes = nodes;
    }


    // ==== public, but for debug purposes

    showDebugInfo(show: boolean): void {
        const sceneCtx = this.scenes.get(this.currentScene);
        if(!sceneCtx) return;
        const { scene } = sceneCtx;
        if(show) {
            import('@babylonjs/inspector').then((inspector) => {
                inspector.Inspector.Show(scene, {});
            });
        } else {
            import('@babylonjs/inspector').then((inspector) => {
                inspector.Inspector.Hide();
            });
        }
    }

    togglePause(): void {
        const sceneCtx = this.scenes.get(this.currentScene);
        if(!sceneCtx) return;
        const { scene } = sceneCtx;
        for(const ag of scene.animationGroups) {
            if(!getMetadataAttr(ag, 'animationFixed')) {
                if(ag.isPlaying) {
                    ag.pause();
                } else {
                    ag.play();
                }
            }
        }
    }

    showReferences(show: boolean): void {
        const sceneCtx = this.scenes.get(this.currentScene);
        if(!sceneCtx) return;
        const { scene } = sceneCtx;
        for(const m of scene.meshes) {
            if(m.name.startsWith(REF_PREFIX)) {
                m.setEnabled(show);
                m.showBoundingBox = show;
            }
        }
    }

    debug(): void {
        // Keep the method, but change what does depending on needs
        for(const scene of this.scenes.values()) {
            for(const node of scene.scene.getNodes()) {
                console.log(node);
            }
        }
    }


    // ==== private

    private renderLoop() {

        if(this.nullRenderLoop) {
            return;
        }

        const sceneCtx = this.scenes.get(this.currentScene);
        if(sceneCtx) {

            // Do not allow the camera go below ground level
            const cpos = sceneCtx.camera.position;
            if(cpos.y < 0) {
                sceneCtx.camera.setPosition(new Vector3(cpos.x, 0, cpos.z));
            }

            if(TMP_LAYOUT_EVERY_FRAME) {
                this.doLayout(sceneCtx, this.nextRelayoutUpdateIn === 0 || sceneCtx.pendingLayout);
                this.doCameraDependentLayout(sceneCtx);
                sceneCtx.pendingLayout = false;
                if(this.nextRelayoutUpdateIn === 0 || sceneCtx.pendingLayout) {
                    this.nextRelayoutUpdateIn = FRAMES_UPDATE_LAYOUT;
                } else {
                    this.nextRelayoutUpdateIn--;
                }

                sceneCtx.pendingLayout = false;
            } else if(sceneCtx.pendingLayout) {
                this.doLayout(sceneCtx, true);
                this.doCameraDependentLayout(sceneCtx);
                sceneCtx.pendingLayout = false;
            }

            if(this.doRender) {
                sceneCtx.scene.render();
            }            

            for(const [sceneId, sceneCtx] of this.scenes.entries()) {
                if(sceneCtx.showAsThumbnail && sceneCtx.thumbnailCamera) {

                    // this scene just paints a white background
                    this.thumbBackgroundScene.activeCamera!.viewport = sceneCtx.thumbnailCamera.viewport;
                    this.thumbBackgroundScene.render();

                    sceneCtx.scene.activeCamera = sceneCtx.thumbnailCamera;
                    sceneCtx.scene.autoClear = false;
                    sceneCtx.scene.render();
                    sceneCtx.scene.autoClear = true;
                    sceneCtx.scene.activeCamera = sceneCtx.camera;
                }
            }

        }
    }

    private addSSAO(scene: Scene, cameras: Camera[]) {
        // Create SSAO and configure all properties (for the example)
        var ssaoRatio = {
            ssaoRatio: 2.0,
            combineRatio: 1.0
        };

        var ssao = new SSAORenderingPipeline("ssao", scene, ssaoRatio, cameras);
        //ssao.fallOff = 0.000001;
        //ssao.area = 1;
        //ssao.radius = 0.0001;
        //ssao.totalStrength = 1.0;
        //ssao.base = 0.5;

    }

    private create3DCamera(scene: Scene) {
        const camera = new ArcRotateCamera(ORBITING_CAMERA_ID, 0, 0, 1, ORIGIN, scene);
        camera.minZ = CAMERA_SETTINGS.minZ;
        camera.maxZ = CAMERA_SETTINGS.maxZ;
        camera.speed = CAMERA_SETTINGS.speed;
        //camera.wheelDeltaPercentage = CAMERA_SETTINGS.wheelDeltaPercentage;
        camera.panningSensibility = CAMERA_SETTINGS.panningSensibility;
        camera.wheelPrecision = CAMERA_SETTINGS.wheelPrecision;
        camera.useNaturalPinchZoom = true;
        camera.checkCollisions = CAMERA_SETTINGS.checkCollisions;
        camera.collisionRadius = CAMERA_SETTINGS.collisionRadius;
        camera.upperBetaLimit = CAMERA_SETTINGS.upperBetaLimit;
        camera.lowerRadiusLimit = CAMERA_SETTINGS.lowerRadiusLimit;
        camera.upperRadiusLimit = CAMERA_SETTINGS.upperRadiusLimit;
        camera.onViewMatrixChangedObservable.add(debounce(() => {
            if(this.broadcastEvents) {
                const cameraData = {
                    target: camera.target.asArray(),
                    position: camera.position.asArray()
                }
                EventEmitter.dispatch(eventTypes.VIEWER_EVENTS, events.CAMERA_CHANGE, cameraData);    
            }
        }, 125));
        return camera;
    }

    private fit3DCamera(sceneCtx: SceneContext, immediate: boolean) {
        const { scene, camera } = sceneCtx;
        const [ position, target ] = this.calculateFit3DCamera(scene, camera);

        if(immediate) {
            camera.position = position;
            camera.target = target;
        } else {
            this.animateCamera(camera, target, position);
        }
    }

    private calculateFit3DCamera(scene: Scene, camera: ArcRotateCamera): [ Vector3, Vector3 ] {
        const { min, max } = scene.getWorldExtends(isBoundingBoxMesh);
        const centerX = ( min.x + max.x ) / 2;
        const centerY = ( min.y + max.y ) / 2;
        const centerZ = ( min.z + max.z ) / 2;
        const sizeX = max.x - min.x;
        const sizeY = max.y - min.y;            
        const position = camera.position.clone();
        const target =camera.target.clone();

        // adapted from https://stackoverflow.com/questions/2866350/move-camera-to-fit-3d-scene
        const d = (Math.max(sizeX,sizeY) / 2) / Math.tan(camera.fov / 2);
        const radius = d * CAMERA_FIT_MARGIN_FACTOR;

        position.x = centerX;
        position.y = centerY + Math.sin(CAMERA_SETTINGS.fitAngle) * radius;
        position.z = centerZ + Math.cos(CAMERA_SETTINGS.fitAngle) * radius;

        target.x = centerX;
        target.y = centerY;
        target.z = centerZ;

        return [ position, target ];
    }

    private createTopCamera(scene: Scene) {
        const camera = new ArcRotateCamera(TOP_CAMERA_ID, 0, TOP_CAMERA_SETTINGS.beta, TOP_CAMERA_SETTINGS.radius, new Vector3(0,0,0), scene);
        camera.target = new Vector3(0, 0, 0);
        camera.position = new Vector3(0, 0, TOP_CAMERA_SETTINGS.radius);
        camera.upVector = new Vector3(0, 0, -1);
        camera.minZ = TOP_CAMERA_SETTINGS.minZ;
        camera.maxZ = TOP_CAMERA_SETTINGS.maxZ;
        camera.lowerAlphaLimit = TOP_CAMERA_SETTINGS.alpha;
        camera.upperAlphaLimit = TOP_CAMERA_SETTINGS.alpha;
        camera.lowerBetaLimit = TOP_CAMERA_SETTINGS.beta;
        camera.upperBetaLimit = TOP_CAMERA_SETTINGS.beta;
        camera.wheelDeltaPercentage = TOP_CAMERA_SETTINGS.wheelDeltaPercentage;
        camera.lowerRadiusLimit = TOP_CAMERA_SETTINGS.lowerRadiusLimit;
        camera.upperRadiusLimit = TOP_CAMERA_SETTINGS.upperRadiusLimit;
        camera.fov = TOP_CAMERA_SETTINGS.fov;
        return camera;
    }

    private fitTopCamera(sceneCtx: SceneContext) {
        const { scene, topCamera: camera } = sceneCtx;
        const { min, max } = scene.getWorldExtends(isBoundingBoxMesh);
        const centerX = ( min.x + max.x ) / 2;
        const centerY = ( min.y + max.y ) / 2;
        const centerZ = ( min.z + max.z ) / 2;
        const sizeX = max.x - min.x;
        const sizeZ = max.z - min.z;            
        const position = camera.position.clone();
        const target =camera.target.clone();
        position .x = centerX;
        position.z = centerZ;
        target.x = centerX;
        target.z = centerZ;
        camera.position = position;
        camera.target = target;
        // adapted from https://stackoverflow.com/questions/2866350/move-camera-to-fit-3d-scene
        const d = (Math.max(sizeX,sizeZ) / 2) / Math.tan(camera.fov / 2);
        camera.position.y = centerY + ( d * CAMERA_FIT_MARGIN_FACTOR );
        this.setViewport(camera);
    }

    private setViewport(camera: Camera) {
        camera.viewport = new Viewport(this.cameraOffset.x, this.cameraOffset.y, 1+Math.abs(this.cameraOffset.x), 1);
    }

    private animateCamera(camera: ArcRotateCamera, dstTarget: Vector3, dstPosition: Vector3) {
        const ease = new CubicEase();
        ease.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);
        Animation.CreateAndStartAnimation('cam-anim-pos', camera, 'position', CAMERA_FPS, CAMERA_FPS * CAMERA_RESET_DURATION, camera.position, dstPosition, 0, ease);
        Animation.CreateAndStartAnimation('cam-anim-tgt', camera, 'target', CAMERA_FPS, CAMERA_FPS * CAMERA_RESET_DURATION, camera.target, dstTarget, 0, ease);
    }

    private createShadowGenerator(scene: Scene, light: DirectionalLight|undefined): ShadowGenerator | undefined {

        if(light === undefined) {
            return undefined;
        } else {
            switch(this.qualitySettings.shadowGeneratorType) {
                case 'none':
                    return undefined;

                case 'normal':
                    const sg = new ShadowGenerator(this.qualitySettings.shadowResolution, light);
                    this.setShadowFiltering(sg);
                    const shadowMap = sg.getShadowMap();
                    if(shadowMap) {
                        shadowMap.refreshRate = RenderTargetTexture.REFRESHRATE_RENDER_ONEVERYTWOFRAMES;
                    }                    
                    return sg;
    
                case 'cascaded':
                    const csg = new CascadedShadowGenerator(this.qualitySettings.shadowResolution, light);
                    this.setShadowFiltering(csg);
                    const cshadowMap = csg.getShadowMap();
                    if(cshadowMap) {
                        cshadowMap.refreshRate = RenderTargetTexture.REFRESHRATE_RENDER_ONCE;
                    }                    
                    csg.lambda = 0.7;
                    csg.cascadeBlendPercentage = 0.1;
                    csg.stabilizeCascades = true;
                    return csg;
            }    
        }
    }

    private assignParent(node: Node, scene: Scene, parentId: string|undefined) {
        if(parentId) {
            const parent = scene.getNodeById(parentId);
            if(parent) {
                node.parent = parent;
            }
        } else {
            node.parent = null;
        }
    }


    private sortByDependencies(rules: LayoutRule[]): string[] {
        
        const graph: [string,string][] = rules.map(rule => {
            return [ rule.position.referencePart, rule.partId ];
        });
        const order = toposort(graph);
        return order;
    }

    private sortByGroupDependencies(rules: GroupLayoutRule[]): string[] {
        
        const graph: [string,string][] = rules.map(rule => {
            return [ rule.position.referenceGroup, rule.groupId ];
        });
        const order = toposort(graph);
        return order;
    }

    private async applyRuleToMesh(rule: VMeshRuleLoaded, mesh: AbstractMesh, itemId: string, scene: Scene) {
        
        const hide = rule.hide ?? DEFAULT_HIDE;
        const textureScale = rule.textureScale ?? DEFAULT_TEXTURE_SCALE;
        const isIcon = rule.isIcon ?? DEFAULT_IS_ICON;
        const clickable = rule.clickable ?? DEFAULT_CLICKABLE;

        if (hide) {
            mesh.setEnabled(!hide);
        } else {
            const loadTime = getMetadataAttr(mesh, 'loadTime');
            if (loadTime) {
                mesh.setEnabled(loadTime.enabled);
            }
        }

        setMetadataAttr(mesh, ATTR_CLICKABLE, clickable);
        setMetadataAttr(mesh, ATTR_EXCLUDE_FROM_BBOX_MESH_RULE, rule.ignoreInLayout ?? false);

        if(rule.data) {
            setMetadataAttr(mesh, ATTR_DATA, rule.data);
        }

        if(textureScale !== 1) {
            const alreadyScaled = getMetadataAttr(mesh, ATTR_UV_ALREADY_SCALED);
            if(!alreadyScaled) {
                setUVScale(mesh, 1/textureScale, 1/textureScale);
                setMetadataAttr(mesh, ATTR_UV_ALREADY_SCALED, true);
            }
        }

        if(isIcon) {
            const existingMaterial = mesh.material;
            if(existingMaterial instanceof PBRMaterial) {
                const iconMaterial = existingMaterial as PBRMaterial;
                iconMaterial.useAlphaFromAlbedoTexture = true;
                if(iconMaterial.albedoTexture) {
                    iconMaterial.albedoTexture.hasAlpha = true;
                }
                iconMaterial.transparencyMode = Material.MATERIAL_ALPHATESTANDBLEND;
            }
        }

        if (rule.material) {
            const rm = rule.material;
            const name = rm.name;

            let m: Material | null = null;
            if(name) {
                m = scene.getMaterialByName(name);
            }

            if(m) {
                mesh.material = m;
            } else {
                const pbrm = new PBRMaterial(name ?? `${itemId}-${rule}`, scene);

                if(rm.alpha !== undefined && rm.alpha < 0.9) {
                    // the 0.9 threshold is because lots of vizoo materials have >0.99 alphas,
                    // not visually significative but that may impact performance
                    pbrm.alpha = rm.alpha;
                }
                if(!rm.baseAndAlpha) {
                    pbrm.albedoColor = new Color3(rm.basecolor[0], rm.basecolor[1], rm.basecolor[2]);
                }                
                // TODO anisotropy
                pbrm.clearCoat.roughness = rm.clearcoat.roughness;
                // TODO displacement
                pbrm.indexOfRefraction = rm.ior;
                pbrm.metallic = rm.metalness;
                pbrm.roughness = rm.roughness;
                pbrm.sheen.intensity = rm.sheen.value;
                pbrm.specularIntensity = rm.specular.value;
                pbrm.metallicReflectanceColor = new Color3(rm.metallicReflectanceColor[0], rm.metallicReflectanceColor[1], rm.metallicReflectanceColor[2]);
    
                pbrm.useParallax = true;
                pbrm.parallaxScaleBias = 0.01;
    
                pbrm.useRoughnessFromMetallicTextureAlpha = false;
                pbrm.useRoughnessFromMetallicTextureGreen = true;
                pbrm.useMetallnessFromMetallicTextureBlue = true;
    
                let albedoTextureP: Promise<Texture | null>;
                let metallicTextureP: Promise<Texture | null>;
                let bumpTextureP: Promise<Texture | null>;
                let aoTextureP: Promise<Texture | null>

                if (rm.baseAndAlpha) {
                    albedoTextureP = this.createTexture(rm.baseAndAlpha, scene);                    
                } else {
                    albedoTextureP = Promise.resolve(null);
                }

                if (rm.metalnessAndRoughness) {
                    metallicTextureP = this.createTexture(rm.metalnessAndRoughness, scene);
                } else {
                    metallicTextureP = Promise.resolve(null);
                }

                if (rm.normalAndHeight) {
                    bumpTextureP = this.createTexture(rm.normalAndHeight, scene);
                } else {
                    bumpTextureP = Promise.resolve(null);
                }

                if (rule.aoMap) {
                    aoTextureP = this.createAOTexture(rule.aoMap, scene);
                } else {
                    aoTextureP = Promise.resolve(null);
                }

                const [ albedoTexture, metallicTexture, bumpTexture, aoTexture ] = await Promise.all([albedoTextureP, metallicTextureP, bumpTextureP, aoTextureP]);
                if(albedoTexture) {
                    pbrm.albedoTexture = albedoTexture;
                }
                if(metallicTexture) {
                    pbrm.metallicTexture = metallicTexture;
                }
                if(bumpTexture) {
                    pbrm.bumpTexture = bumpTexture;
                    pbrm.invertNormalMapY = true;
                }
                if(aoTexture) {
                    pbrm.ambientTexture = aoTexture;
                    pbrm.ambientTextureStrength = 1;
                }

                pbrm.metadata = {
                    namedMaterial: true
                }

                await pbrm.forceCompilationAsync(mesh);

                if(this.wireframe) {
                    pbrm.wireframe = true;
                }

                mesh.material = pbrm;

            }

        }
    }

    // layout

    /**
     * Updates the layout unless the user has indicated that it is in an update
     * and, in that case, the layout is not done.
     *
     * Internally in this object this should be called when there are modifications
     * with potential layout changing results.
     * 
     * The actual layout is done in doLayout
     */

    private updateLayout(sceneCtx: SceneContext) {
        if(this.inUpdate) {
            sceneCtx.pendingLayout = true;
            sceneCtx.pendingGlobalDimensions = true;
        } else {
            this.doLayout(sceneCtx, true);
            this.updateGlobalDimensions(sceneCtx);
        }
    }

    /** 
     * Does the actual layout (reposition some objects according to others).
     * 
     * Do not call this internally, call updateLayout.
     */
    private doLayout(sceneCtx: SceneContext, notify: boolean) {

        const { scene, layoutRules, groupLayoutRules } = sceneCtx;
        this.doSetAllAnimationPercentages(scene, 0);

        const orderedParts = this.sortByDependencies(layoutRules);
        for(const currentPartId of orderedParts) {
            const rules = layoutRules.filter(r => r.partId === currentPartId);
            const currentPart = scene.getNodeById(currentPartId);
            for(const rule of rules) {
                const referencePart = scene.getNodeById(rule.position.referencePart);
                if(currentPart && referencePart && 
                    currentPart instanceof TransformNode && referencePart instanceof TransformNode) {                    
                    if('referenceMesh' in rule.position) {
                        this.layoutByMatchingMeshes(currentPart, referencePart, rule.position);
                    } else if('bbox' in rule.position) {
                        this.layoutByBoundingBox(currentPart, referencePart, rule.position);
                    } else {
                        this.layoutByCopyPosition(currentPart, referencePart, rule.position);
                    }
                    updateWorldMatrixInAllMeshes(currentPart);
                }
            }
        }

        const layoutResults: Record<string, ElementLayoutResult> = {};

        const orderedGroups = this.sortByGroupDependencies(groupLayoutRules);
        for(const currentGroupId of orderedGroups) {
            const rules = groupLayoutRules.filter(r => r.groupId === currentGroupId);
            for(const rule of rules) {
                const currentGroup = scene.getNodeById(rule.groupId);
                const referenceGroup = scene.getNodeById(rule.position.referenceGroup);
                if(currentGroup && referenceGroup && 
                    currentGroup instanceof TransformNode && referenceGroup instanceof TransformNode) {                    
                    this.layoutGroupByBoundingBox(currentGroup, referenceGroup, rule.position.side, rule.position.align, rule.position.offset ?? 0);                    
                    layoutResults[currentGroupId] = {
                        position: toV3Coord(currentGroup.getAbsolutePosition()),
                        rotation: toV3Rotation(currentGroup.absoluteRotationQuaternion.toEulerAngles())
                    }
                }
            }
        }

        this.updateGlobalDimensions(sceneCtx);

        this.repositionReferenced(sceneCtx);
        this.doSetAllAnimationPercentages(scene, this.animationPercentagesByScene.get(sceneCtx.sceneId) ?? 0);

        if((notify ?? true) && this.broadcastEvents) {
            const watchedDimensions: Record<string, WatchedElementResult> = {};
            for(const nodeId of this.watchDimensionsNodes) {
                const node = scene.getTransformNodeById(nodeId);
                if(node) {
                    const [ min, max ] = getLimitsTransformed(node, false);
                    const sizeX = max.x - min.x;
                    const sizeZ = max.z - min.z;            
                    watchedDimensions[nodeId] = {
                        boundingBox: [
                            toV3Coord(min),
                            toV3Coord(max)
                        ],
                        dimensions: [ sizeX, sizeZ ]
                    }
                }
            }

            EventEmitter.dispatch(eventTypes.VIEWER_EVENTS, events.LAYOUT_RESULTS, {
                layout: layoutResults,
                dimensions: watchedDimensions
            });

        }
    }

    private repositionReferenced(sceneCtx: SceneContext) {
        const { scene } = sceneCtx;
        for(const tn of scene.transformNodes) {
            const positionedBy = getMetadataAttr(tn, ATTR_POSITIONED_BY);
            if(positionedBy) {
                const reference = scene.getTransformNodeById(positionedBy);
                if(reference) {
                    const targetPosition = reference.getAbsolutePosition();
                    if(targetPosition.subtract(tn.position).length() > 0.01) {
                        tn.position = targetPosition;
                    }
                } else {
                    tn.dispose();
                }
            }
        }
    }

    private doCameraDependentLayout(sceneCtx: SceneContext) {
        const { scene } = sceneCtx;
        const camera = scene.activeCamera;
        if(!camera) {
            return;
        }
        this.doCameraDependentLayoutAux(scene, camera, SHOW_4_DIMENSIONS && camera === sceneCtx.camera);
    }

    private doCameraDependentLayoutAux(scene: Scene, camera: Camera, layoutAllDimensionSides: boolean) {

        // The problem here is that we are fixing the size in 3d, but it still 
        // has to be consistent with the UI interface event if the canvas is small
        const canvasFactor = 1024 / Math.min(this.canvas.width, this.canvas.height);

        for(const tn of scene.transformNodes) {
            const fixedSizeFactor = getMetadataAttr(tn, ATTR_FIXED_SIZE_FACTOR);
            if(fixedSizeFactor) {
                const fov = camera.fov;
                const cameraObjectDistance = Vector3.Distance(camera.position, tn.position);
                const worldSize = (2 * Math.tan(fov / 2)) * cameraObjectDistance;
                const size = worldSize * fixedSizeFactor * canvasFactor;
                tn.scaling = new Vector3(size, size, size);
            }
        }

        if(layoutAllDimensionSides) {
            const arcRotateCamera = camera as ArcRotateCamera;
            const cameraVector = camera.position.subtract(arcRotateCamera.target);
            cameraVector.y = 0;
            cameraVector.normalize();
            
            for(const m of scene.meshes) {
                if(m.id.indexOf('--dimensions-t') > 0) {
                    const rotation = m.absoluteRotationQuaternion;
                    const textVector = new Vector3(0, 1, 0).applyRotationQuaternion(rotation);
                    textVector.normalize();
                    const angle = Math.acos(Vector3.Dot(cameraVector, textVector));
                    m.setEnabled(angle > ((Math.PI / 2) * 0.9));
                
                }
            }    
        } else {
            for(const m of scene.meshes) {
                if(m.id.indexOf('--dimensions-t') > 0 && !m.id.startsWith('--global-dimensions')) {
                    let q = m.absoluteRotationQuaternion;
                    let rotation = q.toEulerAngles().y;
                    while(rotation < 0 || rotation > (Math.PI * 2)) {
                        if(rotation < 0) {
                            rotation += Math.PI * 2;
                        } else {
                            rotation -= Math.PI * 2;
                        }
                    }
                    m.setEnabled(
                        Math.abs(rotation - Math.PI) < 0.01 ||
                        Math.abs(rotation - Math.PI / 2) < 0.01
                    );
                }
            }    
        }
        
    }

   
    private layoutByMatchingMeshes(currentPart: TransformNode, referencePart: TransformNode, position: VRelativePositioningMeshes) {
        const refMeshName = position.referenceMesh;
        const currentMeshName = position.mesh;
        const referenceMeshes = referencePart.getChildMeshes(false, m => {
            return m.name == refMeshName}
        );
        const currentMeshes = currentPart.getChildMeshes(false, m => m.name == currentMeshName);
        if(referenceMeshes.length === 1 && currentMeshes.length === 1) {
            const refMesh = referenceMeshes[0];
            const currentMesh = currentMeshes[0]; 
            refMesh.refreshBoundingInfo();
            currentMesh.refreshBoundingInfo();
            if(this.hideReferences) {
                refMesh.setEnabled(false);
                currentMesh.setEnabled(false);
            }            
            const refPos = refMesh.getAbsolutePosition();
            const currentPos = currentMesh.getAbsolutePosition();
            const diff = refPos.add(currentPos.negate());
            const newPosition = referencePart.position.add(diff);
            const finalPosition = newPosition.add(toVector3(position.delta));
            currentPart.position = finalPosition;
        }
    }

    private layoutByBoundingBox(currentPart: TransformNode, referencePart: TransformNode, position: VRelativePositioningBoundingBox) {
        const currentBVectors = currentPart.getHierarchyBoundingVectors(true);
        const refBVectors = referencePart.getHierarchyBoundingVectors(true);
        // only valid value for now is top
        const yDiffWithPosition = currentPart.position.y - currentBVectors.min.y;
        const stackCoord = new Vector3(
            position.delta[0],
            position.delta[1] + refBVectors.max.y + yDiffWithPosition,
            position.delta[2]
        )
        currentPart.position = stackCoord;
    }

    private layoutGroupByBoundingBox(currentGroup: TransformNode, 
                                     referenceGroup: TransformNode, 
                                     side: VGroupRelativePositionSide, 
                                     align: VGroupRelativePositionSide | undefined,
                                     offset: number) {

        updateWorldMatrixInAllMeshes(referenceGroup);
        updateWorldMatrixInAllMeshes(currentGroup);

        const [ refMin, refMax ] = getLimitsTransformed(referenceGroup, false);
        const [ curMin, curMax ] = getLimitsTransformed(currentGroup, true);

        const refRotation = referenceGroup.rotation.y;
        const curRotation = currentGroup.rotation.y;

        let refOffset: number;
        let groupOffset: number;

        let xOffset = 0;
        let yOffset = 0;
        let zOffset = 0;

        switch(side) {

            case 'right':
                refOffset = offsetFromRotationAndSide(refMin, refMax, refRotation, 'right');
                groupOffset = offsetFromRotationAndSide(curMin, curMax, curRotation, 'left'); 
                xOffset = -(refOffset + groupOffset + offset);
                break;

            case 'left':
                refOffset = offsetFromRotationAndSide(refMin, refMax, refRotation, 'left');
                groupOffset = offsetFromRotationAndSide(curMin, curMax, curRotation, 'right');
                xOffset = refOffset + groupOffset + offset;
                break;

            case 'front':
                refOffset = offsetFromRotationAndSide(refMin, refMax, refRotation, 'front');
                groupOffset = offsetFromRotationAndSide(curMin, curMax, curRotation, 'back');
                zOffset = refOffset + groupOffset + offset 
                break;

            case 'back':
                refOffset = offsetFromRotationAndSide(refMin, refMax, refRotation, 'back');
                groupOffset = offsetFromRotationAndSide(curMin, curMax, curRotation, 'front');
                zOffset = -(refOffset + groupOffset + offset);
                break;

            case 'top':
                refOffset = refMax.y;
                groupOffset = -curMin.y;
                yOffset = refOffset + groupOffset;
                break;
        }

        switch(align) {

            case 'right':
                refOffset = offsetFromRotationAndSide(refMin, refMax, refRotation, 'right');
                groupOffset = offsetFromRotationAndSide(curMin, curMax, curRotation, 'right'); 
                xOffset = -(refOffset - groupOffset);
                break;

            case 'left':
                refOffset = offsetFromRotationAndSide(refMin, refMax, refRotation, 'left');
                groupOffset = offsetFromRotationAndSide(curMin, curMax, curRotation, 'left');
                xOffset = refOffset - groupOffset;
                break;

            case 'front':
                refOffset = offsetFromRotationAndSide(refMin, refMax, refRotation, 'front');
                groupOffset = offsetFromRotationAndSide(curMin, curMax, curRotation, 'front');
                zOffset = refOffset - groupOffset 
                break;

            case 'back':
                refOffset = offsetFromRotationAndSide(refMin, refMax, refRotation, 'back');
                groupOffset = offsetFromRotationAndSide(curMin, curMax, curRotation, 'back');
                zOffset = -(refOffset - groupOffset);
                break;

            case 'top':
                refOffset = refMax.y;
                groupOffset = refMax.y;
                yOffset = refOffset - groupOffset;
                break;

        }

        const coord = new Vector3(
            referenceGroup.position.x + xOffset,
            referenceGroup.position.y + yOffset,
            referenceGroup.position.z + zOffset
        );

        /* Uncomment to debug layouts
        console.log(currentGroup.id, {
            offset,
            refMin, refMax, refRotation, 
            curMin, curMax, curRotation,
            refOffset, groupOffset, coord } )
        */

        currentGroup.position = coord;
        updateWorldMatrixInAllMeshes(currentGroup);
    }

    private layoutByCopyPosition(currentPart: TransformNode, referencePart: TransformNode, position: VRelativePositioningPart) {
        currentPart.position = referencePart.position.add(toVector3(position.delta));
    }

    private switchOffScene(sceneId: string) {
        const prevSceneCtx = this.scenes.get(sceneId);
        if(prevSceneCtx) {
            prevSceneCtx.camera.detachControl();
        }
    }

    private doSetAllAnimationPercentages(scene: Scene, percentage: number) {
        for(const ag of scene.animationGroups) {
            const partId = getMetadataAttr(ag, 'partId');
            const fixed = getMetadataAttr(ag, 'animationFixed');
            if(partId && !fixed) {
                const dstFrame = ag.from + ( ag.to - ag.from ) * ( percentage / 100 );
                ag.goToFrame(dstFrame);
            }
        }
    }


    // textures

    private async createTexture(location: VLocationLoaded|undefined, scene: Scene): Promise<Texture|null> {
        return new Promise((resolve, reject) => {
            if(location) {
                let t: Texture;
                const onLoad = function() {
                    if(t) {
                        t.uScale = BASE_TEXTURE_SCALE;
                        t.vScale = BASE_TEXTURE_SCALE;                          
                        resolve(t);
                    } else {
                        reject(new Error(`Couldn't load texture ${location.url}`));
                    }
                };
                const onError = function() {
                    reject(new Error(`Couldn't load texture ${location.url}`));
                }
                t = new Texture(location.url, scene, false, true, undefined, onLoad, onError,
                                     undefined, undefined, undefined, undefined, undefined, undefined, `.${location.ext}`);
            } else {
                return Promise.resolve(null);
            }
        })
    }

    private async createAOTexture(location: VLocationLoaded|undefined, scene: Scene): Promise<Texture|null> {
        return new Promise((resolve, reject) => {
            if(location) {
                let t: Texture;
                const onLoad = function() {
                    if(t) {
                        resolve(t);
                    } else {
                        reject(new Error(`Couldn't load texture ${location.url}`));
                    }
                };
                const onError = function() {
                    reject(new Error(`Couldn't load texture ${location.url}`));
                }
                t = new Texture(location.url, scene, undefined, false, undefined, onLoad, onError,
                                     undefined, undefined, undefined, undefined, undefined, undefined, `.${location.ext}`);
            } else {
                return Promise.resolve(null);
            }
        })
    }



    // shadows

    private makeAllMeshesShadowCaster(scene: Scene, sg: ShadowGenerator|undefined) {
        if(sg) {
            for(const m of scene.meshes) {
                sg.addShadowCaster(m);
            }    
        }
    }

    private setShadowFiltering(sg: ShadowGenerator): void {
        switch(this.qualitySettings.shadowFiltering) {
            case 'poisson':
                sg.usePoissonSampling = true;
                break;

            case 'exp-sm':
                sg.usePoissonSampling = true;
                sg.useExponentialShadowMap = true;
                break;

            case 'blur-exp-sm':
                sg.usePoissonSampling = true;
                sg.useBlurExponentialShadowMap = true;
                break;

            case 'blur-exp-kernel':
                sg.usePoissonSampling = true;
                sg.useBlurExponentialShadowMap = true;
                sg.useKernelBlur = true;
                break;

            case 'close-exp':
                sg.useCloseExponentialShadowMap = true;
                break;

            case 'pcf':
                sg.usePercentageCloserFiltering = true;
                break;

            case 'contact-hard':
                sg.useContactHardeningShadow = true;
                sg.filteringQuality = ShadowGenerator.QUALITY_LOW;
                break;
            
        }
    }

    private refreshShadowMap(sceneCtx: SceneContext) {
        let shadowMap = sceneCtx.shadowgen?.getShadowMap();
        if(shadowMap) {
            shadowMap.resetRefreshCounter();
        }
    }



    // dimensions

    private updateGroupDimensions(sceneCtx: SceneContext, groupId: string) {
        if(this.inUpdate) {
            sceneCtx.pendingGroupDimensionsUpdate.add(groupId);
        } else {
            this.doGroupDimensions(sceneCtx, groupId);
        }
    }

    private doGroupDimensions(sceneCtx: SceneContext, groupId: string) {

        const node = sceneCtx.scene.getTransformNodeById(groupId);
        if(!node) return;

        const deletePreviousDimensions = () => {
            for(const m of node.getChildMeshes()) {
                if(m.name.startsWith(`${groupId}--dimensions-`)) {
                    m.dispose();
                }
            }
        }

        const showDimensions = getMetadataAttr(node, ATTR_SHOW_DIMENSIONS, false);
        const showDimensionsApplied = getMetadataAttr(node, ATTR_SHOW_DIMENSIONS_APPLIED, false);

        if(showDimensionsApplied && !showDimensions) {
            // if there were dimensions and now not, delete it!
            deletePreviousDimensions();
            setMetadataAttr(node, ATTR_SHOW_DIMENSIONS_APPLIED, false);
        } else if(showDimensions) {

            const { scene } = sceneCtx;

            const [ min, max ] = getLimitsTransformed(node, false);
            const dimCalcs = this.calcDimensions(min, max);
            const forcedDimensions = getMetadataAttr(node, ATTR_DIMENSIONS);
            
            if(forcedDimensions && forcedDimensions[0]) {
                console.log('Forced dimensions found for X:', forcedDimensions[0], 'Unit system:', this.unitSystem);
                if (this.unitSystem === 'inch') {
                    // Pass raw meter value - conversion will happen in createDimensionTexts
                    dimCalcs.textX = forcedDimensions[0].toString();
                    console.log('Set textX to raw meter value:', dimCalcs.textX);
                } else {
                    dimCalcs.textX = `${forcedDimensions[0].toFixed(2)} m.`;
                }
            }

            if(forcedDimensions && forcedDimensions[2]) {
                console.log('Forced dimensions found for Z:', forcedDimensions[2], 'Unit system:', this.unitSystem);
                if (this.unitSystem === 'inch') {
                    // Pass raw meter value - conversion will happen in createDimensionTexts
                    dimCalcs.textZ = forcedDimensions[2].toString();
                    console.log('Set textZ to raw meter value:', dimCalcs.textZ);
                } else {
                    dimCalcs.textZ = `${forcedDimensions[2].toFixed(2)} m.`;
                }
            }
    
            if(showDimensionsApplied) {
                const limitsDiff = diff(showDimensionsApplied, dimCalcs) as any;
                if(limitsDiff.centerX === undefined || 
                   limitsDiff.centerY === undefined || 
                   limitsDiff.sizeX === undefined ||
                   limitsDiff.sizeZ === undefined || 
                   limitsDiff.xStr  === undefined || 
                   limitsDiff.zStr === undefined) {
                    deletePreviousDimensions();
                } else {
                    return;
                }
            }

            const material = this.getSelectionMaterial(sceneCtx);
            const metadata = {
                [ATTR_USE_IN_BBOX]: false,
                [ATTR_IS_DIMENSION]: true,
                [ATTR_HIDE_IN_SNAPSHOT]: true
            }
            this.createDimensionLines(scene, groupId, node, 0, dimCalcs, material, metadata);
            this.createDimensionTexts(scene, groupId, node, 0, true, dimCalcs, material, metadata);
            setMetadataAttr(node, ATTR_SHOW_DIMENSIONS_APPLIED, dimCalcs);
        }
        
    }

    private updateGlobalDimensions(sceneCtx: SceneContext) {
        if(this.inUpdate) {
            sceneCtx.pendingGlobalDimensions = true;
        } else {
            this.doGlobalDimensions(sceneCtx);
        }
    }

    private doGlobalDimensions(sceneCtx: SceneContext) {

        if(sceneCtx.globalDimensions) {
            const { scene } = sceneCtx;
            let gd = scene.getTransformNodeById(GLOBAL_DIMENSIONS_ID);

            const { min, max } = scene.getWorldExtends(isBoundingBoxMesh);
            const dimCalcs = this.calcDimensions(min, max);

            const sameSize = sceneCtx.appliedGlobalDimensions === undefined ? false : this.isSameSize(dimCalcs, sceneCtx.appliedGlobalDimensions);
            const samePosition = sceneCtx.appliedGlobalDimensions === undefined ? false : this.isSamePosition(dimCalcs, sceneCtx.appliedGlobalDimensions);
            const sameText = sceneCtx.appliedGlobalDimensions === undefined ? false : this.isSameText(dimCalcs, sceneCtx.appliedGlobalDimensions);

            if(sameSize && samePosition && sameText) {
                return;
            } else {
                if(!gd) {
                    gd = new TransformNode(GLOBAL_DIMENSIONS_ID, scene);
                }
                const material = this.getGlobalSelectionMaterial(sceneCtx);
                const metadata = {
                    [ATTR_USE_IN_BBOX]: false,
                    [ATTR_IS_DIMENSION]: true,
                }

                if(!sameSize) {
                    this.deleteDimensionLines(gd);
                    this.createDimensionLines(scene, GLOBAL_DIMENSIONS_ID, gd, 1, dimCalcs, material, metadata);
                } else if(!samePosition) {
                    this.moveDimensionLines(gd, GLOBAL_DIMENSIONS_ID, 1, dimCalcs);
                }

                if(!sameText) {
                    this.deleteDimensionTexts(gd);
                    this.createDimensionTexts(scene, GLOBAL_DIMENSIONS_ID, gd, 1, SHOW_4_DIMENSIONS, dimCalcs, material, metadata);
                } else if(!samePosition) {
                    this.moveDimensionTexts(gd, GLOBAL_DIMENSIONS_ID, 1, dimCalcs);
                }

            }

            sceneCtx.appliedGlobalDimensions = dimCalcs;
            
        } else {
            const gd = sceneCtx.scene.getTransformNodeById(GLOBAL_DIMENSIONS_ID);
            if(gd) {
                gd.dispose();            
            }
        }
    }

    private calcDimensions(min: Vector3, max: Vector3): DimensionsCalculation {
        const sizeX = max.x - min.x;
        const sizeZ = max.z - min.z;
        const centerX = min.x + (sizeX / 2);
        const centerZ = min.z + (sizeZ / 2);
        
        // For inch unit system, pass raw meter values to be converted later
        // For metric unit system, format with 'm' suffix
        let textX: string;
        let textZ: string;
        
        if (this.unitSystem === 'inch') {
            // Pass raw meter values - conversion will happen in createDimensionTexts
            textX = sizeX.toString();
            textZ = sizeZ.toString();
        } else {
            // Keep in meters for metric countries
            textX = `${sizeX.toFixed(2)} m.`;
            textZ = `${sizeZ.toFixed(2)} m.`;
        }
        
        return { sizeX, sizeZ, centerX, centerZ, textX, textZ };
    }

    private isSameSize(dim1: DimensionsCalculation, dim2: DimensionsCalculation): boolean {
        return dim1.sizeX === dim2.sizeX && dim1.sizeZ === dim2.sizeZ;
    }

    private isSamePosition(dim1: DimensionsCalculation, dim2: DimensionsCalculation): boolean {
        return dim1.centerX === dim2.centerX && dim1.centerZ === dim2.centerZ;
    }

    private isSameText(dim1: DimensionsCalculation, dim2: DimensionsCalculation): boolean {
        return dim1.textX === dim2.textX && dim1.textZ === dim2.textZ;
    }

    /**
     * Set the unit system for dimension display
     * @param unit - 'cm' or 'inch'
     */
    public setUnitSystem(unit: 'cm' | 'inch'): void {
        if (this.unitSystem !== unit) {
            this.unitSystem = unit;
            // Trigger dimension update to refresh display
            const sceneCtx = this.scenes.get(this.currentScene);
            if (sceneCtx) {
                this.updateLayout(sceneCtx);
            }
        }
    }

    /**
     * Get the current unit system
     * @returns current unit system
     */
    public getUnitSystem(): 'cm' | 'inch' {
        return this.unitSystem;
    }

    private createDimensionLines(
        // where
        scene: Scene, idBase: string, parent: Node, position: number,
        // what
        spec: DimensionsCalculation,
        // properties
        material: Material, metadata: any) {

        // sync with move method
        const sizeX = spec.sizeX + (DIMENSION_LINE_WIDTH * position * 2);
        const sizeZ = spec.sizeZ + (DIMENSION_LINE_WIDTH * position * 2);

        const dim1 = CreateGround(`${idBase}--dimensions-l-1`, {
            width: DIMENSION_LINE_WIDTH,
            height: sizeZ + (2 * DIMENSION_LINE_WIDTH)
        }, scene);
        dim1.parent = parent;
        dim1.position = new Vector3(spec.centerX - ( sizeX / 2) - (DIMENSION_LINE_WIDTH / 2), DIMENSION_HEIGHT, spec.centerZ);
        dim1.material = material;
        dim1.metadata = metadata;

        const dim2 = CreateGround(`${idBase}--dimensions-l-2`, {
            width: sizeX,
            height: DIMENSION_LINE_WIDTH
        }, scene);
        dim2.parent = parent;
        dim2.position = new Vector3(spec.centerX, DIMENSION_HEIGHT, spec.centerZ - (sizeZ / 2) - (DIMENSION_LINE_WIDTH / 2));
        dim2.material = material;
        dim2.metadata = metadata;

        const dim3 = CreateGround(`${idBase}--dimensions-l-3`, {
            width: DIMENSION_LINE_WIDTH,
            height: sizeZ + (2 * DIMENSION_LINE_WIDTH)
        }, scene);
        dim3.parent = parent;
        dim3.position = new Vector3(spec.centerX + (sizeX / 2) + (DIMENSION_LINE_WIDTH / 2), DIMENSION_HEIGHT, spec.centerZ);
        dim3.material = material;
        dim3.metadata = metadata;

        const dim4 = CreateGround(`${idBase}--dimensions-l-4`, {
            width: sizeX,
            height: DIMENSION_LINE_WIDTH
        }, scene);
        dim4.parent = parent;
        dim4.position = new Vector3(spec.centerX, DIMENSION_HEIGHT, spec.centerZ + (sizeZ / 2) + (DIMENSION_LINE_WIDTH / 2));
        dim4.material = material;
        dim4.metadata = metadata;
    }

    private moveDimensionLines(gd: Node, idBase: string, position: number, spec: DimensionsCalculation) {
        // sync with create method
        const sizeX = spec.sizeX + (DIMENSION_LINE_WIDTH * position * 2);
        const sizeZ = spec.sizeZ + (DIMENSION_LINE_WIDTH * position * 2);

        const dim1 = gd.getChildMeshes().find(m => m.name === `${idBase}--dimensions-l-1`);
        if(dim1) {
            dim1.position = new Vector3(spec.centerX - ( sizeX / 2) - (DIMENSION_LINE_WIDTH / 2), DIMENSION_HEIGHT, spec.centerZ);
        }
        const dim2 = gd.getChildMeshes().find(m => m.name === `${idBase}--dimensions-l-2`);
        if(dim2) {
            dim2.position = new Vector3(spec.centerX, DIMENSION_HEIGHT, spec.centerZ - (sizeZ / 2) - (DIMENSION_LINE_WIDTH / 2));
        }
        const dim3 = gd.getChildMeshes().find(m => m.name === `${idBase}--dimensions-l-3`);
        if(dim3) {
            dim3.position = new Vector3(spec.centerX + (sizeX / 2) + (DIMENSION_LINE_WIDTH / 2), DIMENSION_HEIGHT, spec.centerZ);
        }
        const dim4 = gd.getChildMeshes().find(m => m.name === `${idBase}--dimensions-l-4`);
        if(dim4) {
            dim4.position = new Vector3(spec.centerX, DIMENSION_HEIGHT, spec.centerZ + (sizeZ / 2) + (DIMENSION_LINE_WIDTH / 2));
        }
    }


    private deleteDimensionLines(parent: Node) {
        for(const m of parent.getChildMeshes()) {
            if(m.name.startsWith(`${parent.id}--dimensions-l-`)) {
                m.dispose();
            }
        }
    }

    private createDimensionTexts(
                // where
                scene: Scene, idBase: string, parent: Node, position: number, allSides: boolean,
                // what
                spec: DimensionsCalculation,
                // properties
                material: Material, metadata: any) {

        if(Number.isFinite(spec.sizeX) && Number.isFinite(spec.sizeZ)) {

            // sync with move dimension texts
            const sizeX = spec.sizeX + (DIMENSION_LINE_WIDTH * position * 2);
            const sizeZ = spec.sizeZ + (DIMENSION_LINE_WIDTH * position * 2);
    
            // Apply unit conversion at the very end
            let displayTextX = spec.textX;
            if (this.unitSystem === 'inch') {
                // Convert from raw meter value to inches
                const meterValue = parseFloat(spec.textX);
                if (!isNaN(meterValue)) {
                    displayTextX = formatMeterToInch(meterValue);
                }
            }
            
            const textX1 = CreateText(`${idBase}--dimensions-t-x`, displayTextX, dimensionsFont,
                {
                    size: DIMENSION_TEXT_SIZE,
                    depth: DIMENSION_TEXT_DEPTH
                }, scene);
            if(textX1) {
                textX1.parent = parent;
                textX1.position = new Vector3(spec.centerX, DIMENSION_HEIGHT, spec.centerZ + (sizeZ/2) + (DIMENSION_TEXT_OFFSET * ( position + 1 )) + DIMENSION_TEXT_SIZE);
                textX1.rotation = new Vector3(Math.PI/2, -Math.PI, 0);
                textX1.material = material;
                textX1.metadata = metadata;    
            }

            // Apply unit conversion at the very end
            let displayTextZ = spec.textZ;
            if (this.unitSystem === 'inch') {
                // Convert from raw meter value to inches
                const meterValue = parseFloat(spec.textZ);
                if (!isNaN(meterValue)) {
                    displayTextZ = formatMeterToInch(meterValue);
                }
            }
            
            const textZ1 = CreateText(`${idBase}--dimensions-t-z`, displayTextZ, dimensionsFont,
                {
                    size: DIMENSION_TEXT_SIZE,
                    depth: DIMENSION_TEXT_DEPTH
                }, scene);
            if(textZ1) {
                textZ1.parent = parent;
                textZ1.position = new Vector3(spec.centerX - (sizeX/2) - (DIMENSION_TEXT_OFFSET * ( position + 1 )) - DIMENSION_TEXT_SIZE, DIMENSION_HEIGHT, spec.centerZ);
                textZ1.rotation = new Vector3(Math.PI/2, Math.PI / 2, 0);
                textZ1.material = material;
                textZ1.metadata = metadata;    
            }

            if(allSides) {
                if(textX1) {
                    const textX2 = textX1.createInstance(`${idBase}--dimensions-t-x2`);
                    textX2.parent = parent;
                    textX2.position = new Vector3(spec.centerX, DIMENSION_HEIGHT, spec.centerZ - (sizeZ/2) - (DIMENSION_TEXT_OFFSET * ( position + 1 )) - DIMENSION_TEXT_SIZE);
                    textX2.rotation = new Vector3(Math.PI/2, 0, 0);
                    textX2.metadata = metadata;
                }
                if(textZ1) {
                    const textZ2 = textZ1.createInstance(`${idBase}--dimensions-t-z2`);
                    textZ2.parent = parent;
                    textZ2.position = new Vector3(spec.centerX + (sizeX/2) + (DIMENSION_TEXT_OFFSET * ( position + 1 )) + DIMENSION_TEXT_SIZE, DIMENSION_HEIGHT, spec.centerZ);
                    textZ2.rotation = new Vector3(Math.PI/2, -Math.PI / 2, 0);
                    textZ2.metadata = metadata;
                }
            }

        }

    }

    private moveDimensionTexts(gd: Node, idBase: string, position: number, spec: DimensionsCalculation) {

        // sync with create dimension texts
        const sizeX = spec.sizeX + (DIMENSION_LINE_WIDTH * position * 2);
        const sizeZ = spec.sizeZ + (DIMENSION_LINE_WIDTH * position * 2);

        const textX1 = gd.getChildMeshes().find(m => m.name === `${idBase}--dimensions-t-x`);
        if(textX1) {
            textX1.position = new Vector3(spec.centerX, DIMENSION_HEIGHT, spec.centerZ + (sizeZ/2) + (DIMENSION_TEXT_OFFSET * ( position + 1 )) + DIMENSION_TEXT_SIZE);
        }
        const textZ1 = gd.getChildMeshes().find(m => m.name === `${idBase}--dimensions-t-z`);
        if(textZ1) {
            textZ1.position = new Vector3(spec.centerX - (sizeX/2) - (DIMENSION_TEXT_OFFSET * ( position + 1 )) - DIMENSION_TEXT_SIZE, DIMENSION_HEIGHT, spec.centerZ);
        }
        const textX2 = gd.getChildMeshes().find(m => m.name === `${idBase}--dimensions-t-x2`);
        if(textX2) {
            textX2.position = new Vector3(spec.centerX, DIMENSION_HEIGHT, spec.centerZ - (sizeZ/2) - (DIMENSION_TEXT_OFFSET * ( position + 1 )) - DIMENSION_TEXT_SIZE);
        }
        const textZ2 = gd.getChildMeshes().find(m => m.name === `${idBase}--dimensions-t-z2`);
        if(textZ2) {
            textZ2.position = new Vector3(spec.centerX + (sizeX/2) + (DIMENSION_TEXT_OFFSET * ( position + 1 )) + DIMENSION_TEXT_SIZE, DIMENSION_HEIGHT, spec.centerZ);
        }

    }

    private deleteDimensionTexts(parent: Node) {
        for(const m of parent.getChildMeshes()) {
            if(m.name.startsWith(`${parent.id}--dimensions-t-`)) {
                m.dispose();
            }
        }
    }


    // material handling

    private getSelectionMaterial(sceneCtx: SceneContext): Material {

        if(!sceneCtx.selectionMaterial) {
            const material: StandardMaterial = new StandardMaterial('selection-material', sceneCtx.scene);
            material.emissiveColor = SELECTION_COLOR;
            material.disableLighting = true;
            sceneCtx.selectionMaterial = material;
        }

        return sceneCtx.selectionMaterial;
    }

    private getGlobalSelectionMaterial(sceneCtx: SceneContext): Material {

        if(!sceneCtx.globalDimensionsMaterial) {
            const material: StandardMaterial = new StandardMaterial('global-selection-material', sceneCtx.scene);
            material.emissiveColor = GLOBAL_DIMENSIONS_COLOR;
            material.disableLighting = true;
            sceneCtx.globalDimensionsMaterial = material;
        }

        return sceneCtx.globalDimensionsMaterial;

    }

    private getShadowCatcherMaterial(sceneCtx: SceneContext): Material {

        if(!sceneCtx.shadowCatcherMaterial) {
            const groundMaterial = new BackgroundMaterial('shadow-catcher-material', sceneCtx.scene);
            groundMaterial.shadowOnly = true;
            groundMaterial.primaryColor = new Color3(0,0,0);
            groundMaterial.alpha = SHADOW_CATCHER_ALPHA;
            sceneCtx.shadowCatcherMaterial = groundMaterial;
        }

        return sceneCtx.shadowCatcherMaterial;

    }




    // pointer events

    private pointerPick(pickInfo: Nullable<PickingInfo>) {
        let node: Nullable<Node> = pickInfo?.pickedMesh ?? null;
        if(node) {                
            while(node && !getMetadataAttr(node, ATTR_CLICKABLE)) {
                node = node.parent;
            }
            const data = getMetadataAttr(node, ATTR_DATA);
            if(this.broadcastEvents) {
                if(data) {
                    EventEmitter.dispatch(eventTypes.VIEWER_EVENTS, events.SELECT_EVENT, data);
                } else {
                    EventEmitter.dispatch(eventTypes.VIEWER_EVENTS, events.EMPTYSELECT_EVENT);
                }
                
            }
        } else {
            if(this.broadcastEvents) {
                EventEmitter.dispatch(eventTypes.VIEWER_EVENTS, events.EMPTYSELECT_EVENT);
            }
        }
    
    }

    private getGroundPosition() {
        const sceneCtx = this.scenes.get(this.currentScene)!;
        const { scene, ground } = sceneCtx;
        var pickinfo = scene.pick(scene.pointerX, scene.pointerY, mesh => {
            return mesh == ground;
        });
        if (pickinfo.hit) {
            return pickinfo.pickedPoint;
        } else {
            return null;
        }
    }

    private pointerDown(pickInfo: Nullable<PickingInfo>) {
        // prerequisite, ignore before having a scene
        if(!this.currentScene) {
            return;
        }
        const sceneCtx = this.scenes.get(this.currentScene)!;
        // determine if there's a moveable object under the pointer
        if(!pickInfo?.hit || pickInfo?.pickedMesh === sceneCtx.ground) {
            return;
        }
        let moveGroup: Nullable<Node> = pickInfo?.pickedMesh;
        while(moveGroup && !getMetadataAttr(moveGroup, ATTR_MOVEABLE)) {
            moveGroup = moveGroup.parent;
        }
        if(!moveGroup) {
            return;
        }
        this.moveGroup = moveGroup as TransformNode;

        this.moveStartingPoint = this.getGroundPosition();
        if (this.moveStartingPoint) { // we need to disconnect camera from canvas
            this.moveDiffReference = this.moveStartingPoint.clone();
            const sceneCtx = this.scenes.get(this.currentScene)!;
            setTimeout(() => {
                sceneCtx.camera.detachControl();
            }, 0);
        }
    }

    private pointerUp() {
        if (this.moveStartingPoint && this.moveDiffReference && this.moveGroup && this.currentScene) {
            const sceneCtx = this.scenes.get(this.currentScene)!;    
            sceneCtx.camera.attachControl(true);
            if(this.moveDiffReference.subtract(this.moveStartingPoint).length() > MOVE_THRESHOLD && this.broadcastEvents) {
                EventEmitter.dispatch(eventTypes.VIEWER_EVENTS, events.MOVE_GROUP, {
                    groupId: this.moveGroup.id,
                    position: toV3Coord(this.moveGroup.getAbsolutePosition())
                });
            }
            this.layout(this.currentScene);
            this.moveGroup = null;
            this.moveStartingPoint = null;
            this.moveDiffReference = null;
        }
    }

    private pointerMove() {
        if (!this.moveDiffReference) {
            return;
        }
        if(!this.moveGroup) {
            return;
        }
        var current = this.getGroundPosition();
        if (!current) {
            return;
        }

        var diff = current.subtract(this.moveDiffReference);
        this.moveGroup.position.addInPlace(diff);

        this.moveDiffReference = current;
        const sceneCtx = this.scenes.get(this.currentScene)!;
        this.repositionReferenced(sceneCtx);
        this.updateGlobalDimensions(sceneCtx);
    }

    // thumbnails

    private createClearThumbnailScene() {
        const scene = new Scene(this.engine);
        const camera = new UniversalCamera(CLEAR_THUMBNAIL_CAMERA_ID, new Vector3(0, 0, -5), scene);
        camera.target = new Vector3(0, 0, 0);
        camera.upVector = new Vector3(0, 1, 0);
        camera.mode = Camera.ORTHOGRAPHIC_CAMERA;
        camera.orthoTop = -0.5;
        camera.orthoBottom = 0.5;
        camera.orthoLeft = 0.5;
        camera.orthoRight = -0.5;
        camera.minZ = 0.1;
        camera.maxZ = 1000;

        const plane = CreatePlane('ground', {
            width: 1,
            height: 1
        }, scene);
        const white = new StandardMaterial('white', scene);
        white.emissiveColor = new Color3(1, 1, 1);
        white.disableLighting = true;
        plane.material = white;
        scene.activeCamera = camera;
        scene.autoClear = false;
        return scene;
    
    }

    private resizeAllThumbnails() {
        for(const sceneCtx of this.scenes.values()) {
            if(sceneCtx.thumbnailCamera) {
                sceneCtx.thumbnailCamera.viewport = this.createThumbnailViewport(sceneCtx.thumbnailPosition, sceneCtx.thumbnailSize);
            }
        }
    }

    /**
     * Calculates the correct (0,1) viewport from pixel coordinates
     */
    private createThumbnailViewport(position: [ number, number], size: [ number, number ]): Viewport {
        const [ x, y ] = position;
        const [ width, height ] = size;
        const vp = new Viewport(x / this.canvas.width, 1 - ((y + height) / this.canvas.height), width / this.canvas.width, height / this.canvas.height);
        return vp;
    }


}

// ==== inner data types ====
// data types used only inside the module

interface LayoutRule { partId: string, position: VRelativePositioning };
interface GroupLayoutRule { groupId: string, position: VGroupRelativePosition };

// Contains information associated to each scene
interface SceneContext {
    sceneId: string
    scene: Scene
    utilLayer: UtilityLayerRenderer
    billboards: TransformNode

    layoutRules: LayoutRule[]
    groupLayoutRules: GroupLayoutRule[]

    pendingLayout: boolean
    pendingGroupDimensionsUpdate: Set<string>
    pendingGlobalDimensions: boolean
    
    globalDimensions: boolean
    appliedGlobalDimensions?: DimensionsCalculation;

    camera: ArcRotateCamera
    topCamera: ArcRotateCamera
    showAsThumbnail?: boolean
    thumbnailCamera?: ArcRotateCamera
    thumbnailPosition: [ number, number ]
    thumbnailSize: [ number, number ]
    ground?: Mesh;

    shadowLight?: DirectionalLight
    shadowgen?: ShadowGenerator

    // fixed materials
    shadowCatcherMaterial?: Material
    selectionMaterial?: Material
    globalDimensionsMaterial?: Material
}

type DimensionsCalculation = {
    sizeX: number
    sizeZ: number
    centerX: number
    centerZ: number
    textX: string
    textZ: string
}



// ==== Math helpers ====

function toV3Coord(v3: Vector3): VCoord {
    return [v3.x, v3.y, v3.z];
}

function toVector3(c: VCoord): Vector3 {
    return new Vector3(c[0], c[1], c[2]);
}

function toScale(s: VCoord): Vector3 {
    return new Vector3(s[0], s[1], s[2]);
}

const DEG_TO_RAD = Math.PI / 180;

function toRotation(r: VRotation): Vector3 {
    return toVector3([
        r[0] * DEG_TO_RAD,
        r[1] * DEG_TO_RAD,
        r[2] * DEG_TO_RAD
    ]);
}

function toV3Rotation(v3: Vector3): VRotation {
    return [
        v3.x / DEG_TO_RAD,
        v3.y / DEG_TO_RAD,
        v3.z / DEG_TO_RAD
    ];
}


// ==== Babylon helpers ====

// taken from https://www.html5gamedevs.com/topic/23576-changing-the-uscale-and%C2%A0vscale-of-the-texture-on-an-object-by-object-basis/?do=findComment&comment=134877
function setUVScale(mesh: AbstractMesh, uScale: number, vScale: number) {
    const UVs = mesh.getVerticesData(VertexBuffer.UVKind);
    if(!UVs) return;
    const len = UVs.length;
    
    if (uScale !== 1) {
        for(let i = 0; i < len; i += 2) {
            UVs[i] *= uScale;
        }
    }
    if (vScale !== 1) {
        for (let i = 1; i < len; i += 2) {
            UVs[i] *= vScale;
        }
    }
    
    mesh.setVerticesData(VertexBuffer.UVKind, UVs);
}

const NO_FILTER = () => true

// Gets the limits of the children (in local space) of a transform node
function getLimitsTransformed(tn: TransformNode, includeAllInBB: boolean): [ Vector3, Vector3 ] {
    if(tn.getChildMeshes().length === 0) {
        return [ Vector3.Zero(), Vector3.Zero() ];
    } else {
        let worldBoundingBox;
        if(includeAllInBB) {
            worldBoundingBox = tn.getHierarchyBoundingVectors(true, isNotDimensionMesh);
        } else {
            worldBoundingBox = tn.getHierarchyBoundingVectors(true, isBoundingBoxMesh);
        }
    
        const worldToLocal = tn.computeWorldMatrix().clone().invert();
        const trMin = Vector3.TransformCoordinates(worldBoundingBox.min, worldToLocal);
        const trMax = Vector3.TransformCoordinates(worldBoundingBox.max, worldToLocal);
        const min = new Vector3(
            Math.min(trMin.x, trMax.x),
            Math.min(trMin.y, trMax.y),
            Math.min(trMin.z, trMax.z)
        );
        const max = new Vector3(
            Math.max(trMin.x, trMax.x),
            Math.max(trMin.y, trMax.y),
            Math.max(trMin.z, trMax.z)
        );
    
        return [ min, max]    
    }
}

function isBoundingBoxMesh(mesh: AbstractMesh): boolean {
    let node: Node|null = mesh;
    const excludeFromBB = getMetadataAttr(mesh, ATTR_EXCLUDE_FROM_BBOX_MESH_RULE);
    do {
        const useInBoundingBox = getMetadataAttr(node, ATTR_USE_IN_BBOX);
        const isDimension = getMetadataAttr(node, ATTR_IS_DIMENSION);
        if(isDimension || excludeFromBB) {
            return false;
        } else if(useInBoundingBox !== undefined) {
            return useInBoundingBox;
        } else {
            node = node.parent;
        }
    } while(node);

    // if never allowed explicitly, then it's not include
    return false;
}

function isNotDimensionMesh(mesh: AbstractMesh): boolean {
    const isDimension = getMetadataAttr(mesh, ATTR_IS_DIMENSION);
    const excludeFromBB = getMetadataAttr(mesh, ATTR_EXCLUDE_FROM_BBOX_MESH_RULE);
    return !(isDimension || excludeFromBB);
}

// metadata helpers

function setMetadataAttr(node: Node|AnimationGroup, key: string, value: any) {
    if(node.metadata) {
        node.metadata[key] = value;
    } else {
        node.metadata = { [key]: value };
    }
}

function getMetadataAttr(node: Node|AnimationGroup|Material|null|undefined, key: string, defaultValue?: any): any {
    return node?.metadata?.[key] ?? defaultValue;
}

function updateWorldMatrixInAllMeshes(node: TransformNode) {
    for(const mesh of node.getChildMeshes()) {
        mesh.computeWorldMatrix(true);
    }
}


// misc helpers

function offsetFromRotationAndSide(min: Vector3, max: Vector3, yRotation: number, side: VGroupRelativePositionSide): number {
    const normalizedRotation = (Math.round(yRotation / DEG_TO_RAD) % 360 + 360) % 360;
    const combined: string = `${side}-${normalizedRotation}`;
    switch(combined) {
        case 'left-0':
        case 'back-90':
        case 'right-180':
        case 'front-270':
            return max.x;

        case 'right-0':
        case 'front-90':
        case 'left-180':
        case 'back-270':
            return -min.x;

        case 'front-0':
        case 'left-90':
        case 'back-180':
        case 'right-270':
            return max.z;

        case 'back-0':
        case 'right-90':
        case 'front-180':
        case 'left-270':
            return -min.z;

        default:
            console.log('Unexpected side-rotation combination', combined);
            return 0;
    }
}

